import React from 'react';
import { SearchIcon, XIcon, ChevronDownIcon, CheckIcon, CheckCircleIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, CircleDotIcon, ShieldIcon } from 'lucide-react';
import { DISPLAY_TYPES, type CaseIssue } from './types';
interface FilterSidebarProps {
  sidebarOpen: boolean;
  typeFilters: Set<string>;
  threadFilter: 'all' | 'terminal' | 'standalone';
  reviewFilter: 'all' | 'reviewed' | 'unreviewed';
  classificationFilter: 'all' | 'hot' | 'critical' | 'tbd' | 'unclassified';
  privilegedFilter: 'all' | 'privileged' | 'not-privileged';
  groupByFamily: boolean;
  hideDuplicates: boolean;
  dateFrom: string;
  dateTo: string;
  participantSearch: string;
  collapsedSections: Set<string>;
  hasActiveFilters: boolean;
  allSenders: string[];
  toggleSection: (section: string) => void;
  toggleTypeFilter: (type: string) => void;
  setThreadFilter: (filter: 'all' | 'terminal' | 'standalone') => void;
  setReviewFilter: (filter: 'all' | 'reviewed' | 'unreviewed') => void;
  setClassificationFilter: (filter: 'all' | 'hot' | 'critical' | 'tbd' | 'unclassified') => void;
  setPrivilegedFilter: (filter: 'all' | 'privileged' | 'not-privileged') => void;
  issueFilter: string | null;
  setIssueFilter: (filter: string | null) => void;
  caseIssues: CaseIssue[];
  docIssues: Record<string, string[]>;
  setGroupByFamily: (v: boolean) => void;
  setHideDuplicates: (v: boolean) => void;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  setParticipantSearch: (v: string) => void;
  commentFilter: 'all' | 'with-comments';
  setCommentFilter: (filter: 'all' | 'with-comments') => void;
  docsWithCommentsCount: number;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  clearAllFilters: () => void;
}
export function FilterSidebar({
  sidebarOpen,
  typeFilters,
  threadFilter,
  reviewFilter,
  classificationFilter,
  privilegedFilter,
  groupByFamily,
  hideDuplicates,
  dateFrom,
  dateTo,
  participantSearch,
  collapsedSections,
  hasActiveFilters,
  allSenders,
  toggleSection,
  toggleTypeFilter,
  setThreadFilter,
  setReviewFilter,
  setClassificationFilter,
  setPrivilegedFilter,
  issueFilter,
  setIssueFilter,
  caseIssues,
  docIssues,
  setGroupByFamily,
  setHideDuplicates,
  setDateFrom,
  setDateTo,
  setParticipantSearch,
  commentFilter,
  setCommentFilter,
  docsWithCommentsCount,
  searchQuery,
  setSearchQuery,
  clearAllFilters
}: FilterSidebarProps) {
  return <div className={`flex flex-col bg-navy-light border-r border-navy-lighter transition-all duration-300 shrink-0 ${sidebarOpen ? 'w-[260px]' : 'w-0 border-r-0'}`} data-id="element-289">
      <div className="flex-1 overflow-y-auto overflow-x-hidden" data-id="element-290">
        <div className="w-[260px]" data-id="element-291">
          {/* Search */}
          <div className="p-3 border-b border-navy-lighter" data-id="element-292">
            <div className="relative" data-id="element-293">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-500" data-id="element-294">
                <SearchIcon className="w-3.5 h-3.5" data-id="element-295" />
              </div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search documents…" className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-xs pl-8 pr-7 py-2 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-296" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-500 hover:text-white" data-id="element-297">
                  <XIcon className="w-3 h-3" data-id="element-298" />
                </button>}
            </div>
          </div>

          {/* Document Type */}
          <div className="border-b border-navy-lighter" data-id="element-299">
            <button onClick={() => toggleSection('type')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-300">
              Document Type
              <div className="flex items-center gap-2" data-id="element-301">
                {typeFilters.size < DISPLAY_TYPES.length && <span className="text-[10px] bg-amber/20 text-amber px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-302">
                    {typeFilters.size} selected
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('type') ? '-rotate-90' : ''}`} data-id="element-303" />
              </div>
            </button>
            {!collapsedSections.has('type') && <div className="px-4 pb-4 space-y-2.5" data-id="element-304">
                {DISPLAY_TYPES.map(type => {
              const isChecked = typeFilters.has(type);
              let dotColor = 'bg-gray-500';
              if (type.includes('Email')) dotColor = 'bg-blue-500';
              if (type.includes('Agreement')) dotColor = 'bg-green-500';
              if (type.includes('Report')) dotColor = 'bg-teal-500';
              if (type.includes('Memo')) dotColor = 'bg-gray-400';
              if (type.includes('Invoice')) dotColor = 'bg-amber';
              return <label key={type} className="flex items-center gap-2.5 cursor-pointer group" data-id="element-305">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isChecked ? 'bg-amber border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-306">
                        {isChecked && <CheckIcon className="w-3 h-3 text-navy" data-id="element-307" />}
                      </div>
                      <div className={`w-2 h-2 rounded-full ${dotColor}`} data-id="element-308" />
                      <span className={`text-sm transition-colors ${isChecked ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-309">
                        {type}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Review Status */}
          <div className="border-b border-navy-lighter" data-id="element-310">
            <button onClick={() => toggleSection('review')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-311">
              Review Status
              <div className="flex items-center gap-2" data-id="element-312">
                {reviewFilter !== 'all' && <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-313">
                    {reviewFilter}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('review') ? '-rotate-90' : ''}`} data-id="element-314" />
              </div>
            </button>
            {!collapsedSections.has('review') && <div className="px-4 pb-4 space-y-2.5" data-id="element-315">
                {([{
              value: 'all',
              label: 'All documents'
            }, {
              value: 'reviewed',
              label: 'Reviewed'
            }, {
              value: 'unreviewed',
              label: 'Not reviewed'
            }] as const).map(({
              value,
              label
            }) => {
              const isSelected = reviewFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setReviewFilter(value)} data-id="element-316">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-green-400' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-317">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-318" />}
                      </div>
                      {value === 'reviewed' && <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" data-id="element-319" />}
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-320">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Classification */}
          <div className="border-b border-navy-lighter" data-id="element-321">
            <button onClick={() => toggleSection('classification')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-322">
              Classification
              <div className="flex items-center gap-2" data-id="element-323">
                {classificationFilter !== 'all' && <span className="text-[10px] bg-amber/20 text-amber px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-324">
                    {classificationFilter}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('classification') ? '-rotate-90' : ''}`} data-id="element-325" />
              </div>
            </button>
            {!collapsedSections.has('classification') && <div className="px-4 pb-4 space-y-2.5" data-id="element-326">
                {([{
              value: 'all',
              label: 'All',
              icon: null,
              color: 'amber'
            }, {
              value: 'hot',
              label: 'Hot',
              icon: FlameIcon,
              color: 'red-400'
            }, {
              value: 'critical',
              label: 'Critical',
              icon: AlertOctagonIcon,
              color: 'orange-400'
            }, {
              value: 'tbd',
              label: 'TBD',
              icon: HelpCircleIcon,
              color: 'yellow-400'
            }, {
              value: 'unclassified',
              label: 'Unclassified',
              icon: CircleDotIcon,
              color: 'gray-500'
            }] as const).map(({
              value,
              label,
              icon: Icon,
              color
            }) => {
              const isSelected = classificationFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setClassificationFilter(value)} data-id="element-327">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-328">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-329" />}
                      </div>
                      {Icon && <Icon className={`w-3.5 h-3.5 text-${color}`} data-id="element-330" />}
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-331">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Privileged */}
          <div className="border-b border-navy-lighter" data-id="element-332">
            <button onClick={() => toggleSection('privileged')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-333">
              Privileged
              <div className="flex items-center gap-2" data-id="element-334">
                {privilegedFilter !== 'all' && <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-335">
                    {privilegedFilter === 'privileged' ? 'yes' : 'no'}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('privileged') ? '-rotate-90' : ''}`} data-id="element-336" />
              </div>
            </button>
            {!collapsedSections.has('privileged') && <div className="px-4 pb-4 space-y-2.5" data-id="element-337">
                {([{
              value: 'all',
              label: 'All documents'
            }, {
              value: 'privileged',
              label: 'Privileged only'
            }, {
              value: 'not-privileged',
              label: 'Non-privileged only'
            }] as const).map(({
              value,
              label
            }) => {
              const isSelected = privilegedFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setPrivilegedFilter(value)} data-id="element-338">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-red-400' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-339">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-340" />}
                      </div>
                      {value === 'privileged' && <ShieldIcon className="w-3.5 h-3.5 text-red-400" data-id="element-341" />}
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-342">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Issues */}
          <div className="border-b border-navy-lighter" data-id="element-343">
            <button onClick={() => toggleSection('issues')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-344">
              Issues
              <div className="flex items-center gap-2" data-id="element-345">
                {issueFilter && <span className="text-[10px] px-1.5 py-0.5 rounded-full normal-case tracking-normal" style={{
                backgroundColor: (caseIssues.find(i => i.id === issueFilter)?.color || '#666') + '20',
                color: caseIssues.find(i => i.id === issueFilter)?.color || '#666'
              }} data-id="element-346">
                    {caseIssues.find(i => i.id === issueFilter)?.label}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('issues') ? '-rotate-90' : ''}`} data-id="element-347" />
              </div>
            </button>
            {!collapsedSections.has('issues') && <div className="px-4 pb-4 space-y-2.5" data-id="element-348">
                <label className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setIssueFilter(null)} data-id="element-349">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${!issueFilter ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-350">
                    {!issueFilter && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-351" />}
                  </div>
                  <span className={`text-sm transition-colors ${!issueFilter ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-352">
                    All documents
                  </span>
                </label>
                {caseIssues.map(issue => {
              const isSelected = issueFilter === issue.id;
              const count = Object.values(docIssues).filter(ids => ids.includes(issue.id)).length;
              return <label key={issue.id} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setIssueFilter(isSelected ? null : issue.id)} data-id="element-353">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? '' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} style={isSelected ? {
                  borderColor: issue.color
                } : {}} data-id="element-354">
                        {isSelected && <div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: issue.color
                  }} data-id="element-355" />}
                      </div>
                      <div className="w-2 h-2 rounded-full shrink-0" style={{
                  backgroundColor: issue.color
                }} data-id="element-356" />
                      <span className={`text-sm transition-colors flex-1 ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-357">
                        {issue.label}
                      </span>
                      <span className="text-[10px] text-gray-600" data-id="element-358">{count}</span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Thread Filters */}
          <div className="border-b border-navy-lighter" data-id="element-359">
            <button onClick={() => toggleSection('thread')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-360">
              Thread Filters
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('thread') ? '-rotate-90' : ''}`} data-id="element-361" />
            </button>
            {!collapsedSections.has('thread') && <div className="px-4 pb-4 space-y-2.5" data-id="element-362">
                {(['all', 'terminal', 'standalone'] as const).map(option => {
              const isSelected = threadFilter === option;
              const label = option === 'all' ? 'All emails' : option === 'terminal' ? 'Terminal only' : 'Standalone only';
              return <label key={option} className="flex items-center gap-2.5 cursor-pointer group" data-id="element-363">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-364">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-365" />}
                      </div>
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-366">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Family View */}
          <div className="border-b border-navy-lighter" data-id="element-367">
            <button onClick={() => toggleSection('family')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-368">
              Family View
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('family') ? '-rotate-90' : ''}`} data-id="element-369" />
            </button>
            {!collapsedSections.has('family') && <div className="px-4 pb-4" data-id="element-370">
                <label className="flex items-center justify-between cursor-pointer group" data-id="element-371">
                  <span className={`text-sm transition-colors ${groupByFamily ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-372">
                    Group by family
                  </span>
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${groupByFamily ? 'bg-amber' : 'bg-navy-lighter'}`} data-id="element-373">
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${groupByFamily ? 'left-[18px]' : 'left-0.5'}`} data-id="element-374" />
                  </div>
                </label>
              </div>}
          </div>

          {/* Duplicate Handling */}
          <div className="border-b border-navy-lighter" data-id="element-375">
            <button onClick={() => toggleSection('duplicates')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-376">
              Duplicate Handling
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('duplicates') ? '-rotate-90' : ''}`} data-id="element-377" />
            </button>
            {!collapsedSections.has('duplicates') && <div className="px-4 pb-4" data-id="element-378">
                <label className="flex items-center justify-between cursor-pointer group" data-id="element-379">
                  <span className={`text-sm transition-colors ${hideDuplicates ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-380">
                    Hide duplicates
                  </span>
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${hideDuplicates ? 'bg-amber' : 'bg-navy-lighter'}`} data-id="element-381">
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${hideDuplicates ? 'left-[18px]' : 'left-0.5'}`} data-id="element-382" />
                  </div>
                </label>
              </div>}
          </div>

          {/* Date Range */}
          <div className="border-b border-navy-lighter" data-id="element-383">
            <button onClick={() => toggleSection('date')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-384">
              Date Range
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('date') ? '-rotate-90' : ''}`} data-id="element-385" />
            </button>
            {!collapsedSections.has('date') && <div className="px-4 pb-4 space-y-3" data-id="element-386">
                <div data-id="element-387">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-388">
                    From
                  </label>
                  <input type="text" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="YYYY-MM-DD" className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-sm px-3 py-1.5 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-389" />
                </div>
                <div data-id="element-390">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-391">To</label>
                  <input type="text" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="YYYY-MM-DD" className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-sm px-3 py-1.5 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-392" />
                </div>
              </div>}
          </div>

          {/* Participants */}
          <div className="border-b border-navy-lighter" data-id="element-393">
            <button onClick={() => toggleSection('participants')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-394">
              Participants
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('participants') ? '-rotate-90' : ''}`} data-id="element-395" />
            </button>
            {!collapsedSections.has('participants') && <div className="px-4 pb-4" data-id="element-396">
                <div className="relative mb-3" data-id="element-397">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-500" data-id="element-398">
                    <SearchIcon className="w-3.5 h-3.5" data-id="element-399" />
                  </div>
                  <input type="text" value={participantSearch} onChange={e => setParticipantSearch(e.target.value)} placeholder="Search email..." className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-sm pl-8 pr-3 py-1.5 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-400" />
                  {participantSearch && <button onClick={() => setParticipantSearch('')} className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-500 hover:text-white" data-id="element-401">
                      <XIcon className="w-3 h-3" data-id="element-402" />
                    </button>}
                </div>

                {/* Suggestions */}
                <div className="flex flex-wrap gap-1.5" data-id="element-403">
                  {allSenders.filter(s => !participantSearch || s.toLowerCase().includes(participantSearch.toLowerCase())).slice(0, 5).map(sender => <button key={sender} onClick={() => setParticipantSearch(sender)} className={`px-2 py-1 rounded text-[10px] transition-colors truncate max-w-full ${participantSearch === sender ? 'bg-amber/20 text-amber border border-amber/30' : 'bg-navy border border-navy-lighter text-gray-400 hover:text-gray-300 hover:bg-navy-hover'}`} title={sender} data-id="element-404">
                        {sender}
                      </button>)}
                </div>
              </div>}
          </div>

          {/* Comments */}
          <div className="border-b border-navy-lighter" data-id="element-405">
            <button onClick={() => toggleSection('comments')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-406">
              Comments
              <div className="flex items-center gap-2" data-id="element-407">
                {commentFilter !== 'all' && <span className="text-[10px] bg-amber/20 text-amber px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-408">
                    filtered
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('comments') ? '-rotate-90' : ''}`} data-id="element-409" />
              </div>
            </button>
            {!collapsedSections.has('comments') && <div className="px-4 pb-4 space-y-2.5" data-id="element-410">
                {[{
              value: 'all' as const,
              label: 'All documents'
            }, {
              value: 'with-comments' as const,
              label: 'With comments'
            }].map(({
              value,
              label
            }) => {
              const isSelected = commentFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setCommentFilter(value)} data-id="element-411">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-412">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-413" />}
                      </div>
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-414">
                        {label}
                      </span>
                      {value === 'with-comments' && <span className="text-[10px] text-gray-600 ml-auto" data-id="element-415">
                          {docsWithCommentsCount}
                        </span>}
                    </label>;
            })}
              </div>}
          </div>
        </div>
      </div>

      {/* Clear All Footer */}
      {hasActiveFilters && <div className="p-4 border-t border-navy-lighter bg-[#0A1520] shrink-0 w-[260px]" data-id="element-416">
          <button onClick={clearAllFilters} className="w-full py-2 text-sm text-amber hover:text-amber-hover transition-colors font-medium" data-id="element-417">
            Clear All Filters
          </button>
        </div>}
    </div>;
}